// About / info affordance (P8).
//
// A small floating (i) button (top-right of the map) that opens a modal panel
// explaining: what the map shows (per-pixel MAX pollen level across the
// selected allergens, over the year by weekly periods), how to read
// the 5-level legend, the data source / provenance, and that this is an
// overview product derived from the 600x800 source maps. Works on desktop and
// mobile (the panel is a centered, scrollable, dismissible dialog).
//
// RED-TEAM ATTRIBUTION FIX (legal-grade, see exec/62-dataset-attributions.md):
//   F1 — EACH region's About credits ITS OWN displayed data sources (the
//        licence-mandated `in_app_required` attribution entries in that region's
//        manifest.provenance.sources[]) verbatim.
//   F3 — the honesty note ("modelled season, not measured intensity") AND the
//        satellite/required attributions are keyed to the VIEWED region's own
//        confidence + sources, so EU/NA/Asia (no model/synthetic void-fill, no
//        satellite-derived data) show NEITHER, while the five fill regions show
//        both. The panel re-renders on map move via `update(center)`.
//   F4 — GBIF + GALORE are pipeline-internal (GBIF DOI is a placeholder); they
//        are NEVER surfaced as in-app user-visible credits.

import { CONFIDENCE_META, type ConfidenceTier } from "../confidence";
import type { ProvenanceSource } from "../manifest";

/** Region bbox in lon/lat (from manifest.geo.bbox) — used to pick the viewed region. */
export interface AboutBbox {
  lon_min?: number;
  lon_max?: number;
  lat_min?: number;
  lat_max?: number;
}

/**
 * Change C — per-region provenance info the About panel renders data-driven.
 * `tier` is the parsed top-level `confidence` string (undefined for tier-less
 * regions). `provenance` is the optional manifest provenance object (read
 * loosely; only a few human-readable fields are surfaced if present).
 */
export interface AboutRegionInfo {
  key: string;
  label: string;
  tier?: ConfidenceTier;
  /**
   * The region's WORST tier present anywhere (manifest `confidenceFloor`). Used
   * (alongside `tier`) to decide whether the modelled/synthetic honesty note is
   * shown — e.g. LatAm/Africa are dominantly `literature` but floor `synthetic`.
   */
  floor?: ConfidenceTier;
  /** Region extent (lon/lat) so the panel can scope content to the viewed region. */
  bbox?: AboutBbox;
  /** Manifest provenance object (shape varies per region; read defensively). */
  provenance?: {
    method?: string;
    honesty?: string;
    note?: string;
    /**
     * The (heterogeneous) `provenance.sources[]` array. Surfaced for the
     * licence-mandated `in_app_required` attributions and dataset credits.
     */
    sources?: ProvenanceSource[];
  };
}

export class AboutPanel {
  /** The floating (i) trigger button — caller mounts it over the map. */
  readonly button: HTMLButtonElement;
  /** The modal overlay (hidden until opened) — caller mounts it on the app. */
  readonly overlay: HTMLElement;

  private readonly regions: AboutRegionInfo[];
  private readonly dialog: HTMLElement;
  private readonly close: HTMLButtonElement;
  /** The key of the region the content is currently scoped to (`undefined` = none). */
  private currentRegionKey: string | undefined;

  /**
   * @param regions Optional per-region info (Change C). The data-driven
   * provenance list is rendered ONLY when at least one region carries a
   * `confidence` tier; otherwise (a hypothetical all-tier-less load) the panel
   * renders the EXACT original hardcoded prose. The honesty note + satellite /
   * required attributions + per-region credits are scoped to the VIEWED region
   * (set via `update(center)`); before the first `update`, the panel renders
   * with no region scoped (no honesty / no satellite / no credits) so it is
   * always safe to open.
   */
  constructor(regions?: AboutRegionInfo[]) {
    this.regions = regions ?? [];
    this.button = document.createElement("button");
    this.button.type = "button";
    this.button.className = "about-btn";
    this.button.setAttribute("aria-label", "About this map");
    this.button.setAttribute("aria-haspopup", "dialog");
    this.button.textContent = "i";

    this.overlay = document.createElement("div");
    this.overlay.className = "about-overlay";
    this.overlay.hidden = true;
    this.overlay.setAttribute("role", "dialog");
    this.overlay.setAttribute("aria-modal", "true");
    this.overlay.setAttribute("aria-label", "About the pollen allergy map");

    this.dialog = document.createElement("div");
    this.dialog.className = "about-dialog";
    this.dialog.innerHTML = buildAboutHtml(this.regions, this.currentRegionKey);

    this.close = document.createElement("button");
    this.close.type = "button";
    this.close.className = "about-close";
    this.close.setAttribute("aria-label", "Close");
    this.close.textContent = "×"; // ×
    this.close.addEventListener("click", () => this.hide());
    this.dialog.appendChild(this.close);

    this.overlay.appendChild(this.dialog);

    this.button.addEventListener("click", () => this.show());
    // Click on the dimmed backdrop (not the dialog) closes.
    this.overlay.addEventListener("click", (e) => {
      if (e.target === this.overlay) this.hide();
    });
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && !this.overlay.hidden) this.hide();
    });
  }

  /**
   * Scope the panel's honesty note + satellite / required attributions + credits
   * to the region under the map center (F3). No-op (re-render skipped) when the
   * viewed region is unchanged, so panning within one region is cheap. Called on
   * map `moveend` (mirrors the confidence badge plumbing).
   */
  update(center: { lng: number; lat: number }): void {
    const region = this.regionAt(center.lng, center.lat);
    const key = region?.key;
    if (key === this.currentRegionKey) return;
    this.currentRegionKey = key;
    this.rebuild();
  }

  /** Re-render the dialog body for the current region, keeping the close button. */
  private rebuild(): void {
    this.dialog.innerHTML = buildAboutHtml(this.regions, this.currentRegionKey);
    this.dialog.appendChild(this.close);
  }

  /** The first region whose bbox contains the point (lon/lat). */
  private regionAt(lng: number, lat: number): AboutRegionInfo | undefined {
    return this.regions.find((r) => {
      const b = r.bbox;
      if (!b) return false;
      const lonMin = b.lon_min ?? -Infinity;
      const lonMax = b.lon_max ?? Infinity;
      const latMin = b.lat_min ?? -Infinity;
      const latMax = b.lat_max ?? Infinity;
      return lng >= lonMin && lng <= lonMax && lat >= latMin && lat <= latMax;
    });
  }

  show() {
    this.overlay.hidden = false;
    const close = this.overlay.querySelector<HTMLElement>(".about-close");
    close?.focus();
  }

  hide() {
    this.overlay.hidden = true;
    this.button.focus();
  }
}

// Change C — the About HTML is split so the "How each region was built"
// provenance section can be EITHER the original hardcoded prose (when no region
// carries a confidence tier) OR a data-driven list (when a region opts in via
// its `confidence` string).
const ABOUT_HEAD = `
  <h2 id="about-title">About this map</h2>

  <p>This is an interactive rebuild of the static
  <strong>Pollen Load Map of Europe</strong>. Pan and zoom over a live map,
  pick the allergens that affect you, and scrub the timeline to see how the
  pollen load changes across the year.</p>

  <h3>What it shows</h3>
  <p>For the allergens you select, each location shows the
  <strong>maximum pollen level across your selection</strong> for the chosen
  period &mdash; so if any of your allergens is high there, the map reads high.
  The year is split into 52 <strong>weekly periods</strong>; the timeline label
  shows the exact date range.</p>

  <h3>How to read the legend</h3>
  <ul class="about-legend">
    <li><span class="about-sw about-sw-0"></span><strong>None</strong> &mdash; no
      pollen; the basemap shows through.</li>
    <li><span class="about-sw" style="background:#8CCC16"></span><strong>Low</strong></li>
    <li><span class="about-sw" style="background:#ECE800"></span><strong>Medium</strong></li>
    <li><span class="about-sw" style="background:#EBAB00"></span><strong>High</strong></li>
    <li><span class="about-sw" style="background:#E22500"></span><strong>Very high</strong></li>
  </ul>
  <p class="about-dim">Sea and areas outside the monitoring network are left
  transparent (no data), so the basemap is visible there.</p>

  <h3>Data source &amp; provenance</h3>
  <p>The pollen data is the European pollen-load climatology published as the
  <em>ContaminationMapEurope</em> widget on
  <a href="https://www.polleninformation.at/" target="_blank" rel="noopener">polleninformation.at</a>,
  produced with the <strong>European Aeroallergen Network (EAN)</strong>
  (Medical University of Vienna). The source maps carry a
  <strong>&copy;&nbsp;2012&nbsp;EAN</strong> mark; we own the underlying data and
  re-present it here. This is an <strong>overview product</strong> derived from
  the 600&times;800 source images &mdash; one per allergen and period &mdash; by
  classifying each pixel back to its 5 pollen levels.</p>
`;

// The ORIGINAL hardcoded provenance section (rendered verbatim when NO region
// carries a confidence tier).
const ABOUT_PROVENANCE_HARDCODED = `
  <h3>How each region was built</h3>
  <p class="about-dim">The three regions come from <strong>different kinds of
  source</strong>, so read them with that in mind:</p>
  <ul class="about-provenance">
    <li><strong>Europe</strong> &mdash; per-pixel levels read directly from the
      original 600&times;800 published pollen-load maps
      (polleninformation.at / EAN). This is the most direct of the three.</li>
    <li><strong>North America</strong> &mdash; derived from the
      <strong>PECM&nbsp;2.0</strong> emission-potential model: a
      <em>where / when likelihood</em> proxy for airborne pollen, not direct
      station measurements.</li>
    <li><strong>Asia (Korea&nbsp;+&nbsp;Japan)</strong> &mdash; a
      <strong>literature-derived seasonal climatology</strong>. Treat the
      magnitudes as approximate; the reliable signal is the <em>timing</em> of
      each season, not the exact level.</li>
  </ul>
`;

const ABOUT_TAIL = `
  <h3>Basemap &amp; geography</h3>
  <p>Basemap tiles by
  <a href="https://openfreemap.org/" target="_blank" rel="noopener">OpenFreeMap</a>
  (<a href="https://openmaptiles.org/" target="_blank" rel="noopener">OpenMapTiles</a>
  schema), &copy;
  <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap contributors</a>.
  Coastline / land mask from
  <a href="https://www.naturalearthdata.com/" target="_blank" rel="noopener">Natural Earth</a>
  (public domain).</p>
`;

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// F4 — datasets that are PIPELINE-INTERNAL and must NEVER be surfaced as an
// in-app user-visible credit. GBIF's per-download DOI in the manifests is a
// placeholder; GALORE is a biological-prior gate, not a displayed-data source.
// Matched case-insensitively against the source/citation text.
function isPipelineInternal(s: ProvenanceSource): boolean {
  const hay = `${s.source ?? ""} ${s.citation ?? ""}`.toLowerCase();
  return hay.includes("gbif") || hay.includes("galore");
}

interface AboutAttributions {
  /**
   * Verbatim, licence-required notices, scoped to the VIEWED region: its own
   * displayed-data credit FIRST (the `displayed_data` entries), then the
   * region's satellite / climate disclaimers (Copernicus/ERA5, ESA WorldCover).
   */
  required: string[];
  /** Dataset credits (NASA MODIS, WorldClim, …) as good-practice citations. */
  credits: string[];
}

/**
 * Collect the licence-required notices + good-practice credits for ONE region
 * (F1/F3/F4). The region's own displayed-data attribution leads; the required
 * notices it actually relies on follow; GBIF/GALORE are filtered (F4). Returns
 * empty arrays for a region with no `sources` (EU) so the section drops out.
 */
function collectAttributions(region: AboutRegionInfo | undefined): AboutAttributions {
  const required: string[] = [];
  const requiredSet = new Set<string>();
  const credits: string[] = [];
  const creditSet = new Set<string>();
  const sources = region?.provenance?.sources;
  if (!Array.isArray(sources)) return { required, credits };

  // Two passes so the region's OWN displayed-data credit always leads, then the
  // remaining required notices (satellite/climate disclaimers), then credits.
  const pushRequired = (text: string) => {
    if (text && !requiredSet.has(text)) {
      requiredSet.add(text);
      required.push(text);
    }
  };
  for (const s of sources) {
    if (!s || typeof s !== "object") continue;
    if (s.in_app_required && s.displayed_data && typeof s.attribution === "string") {
      pushRequired(s.attribution);
    }
  }
  for (const s of sources) {
    if (!s || typeof s !== "object") continue;
    if (isPipelineInternal(s)) continue; // F4
    if (s.in_app_required && !s.displayed_data && typeof s.attribution === "string") {
      pushRequired(s.attribution);
    }
    if (!s.in_app_required && typeof s.citation === "string" && s.citation) {
      if (!creditSet.has(s.citation)) {
        creditSet.add(s.citation);
        credits.push(s.citation);
      }
    }
  }
  return { required, credits };
}

/**
 * The "Data credits & required attributions" section for the VIEWED region.
 * Renders ONLY when that region has a required notice or a credit (so EU — no
 * `sources` — gets nothing, gracefully). The `in_app_required` notices are shown
 * VERBATIM in a set-apart block; the rest are a plain credits list.
 */
function buildAttributionSection(region: AboutRegionInfo | undefined): string {
  const { required, credits } = collectAttributions(region);
  if (required.length === 0 && credits.length === 0) return "";
  let html = `\n  <h3>Data credits &amp; required attributions</h3>\n`;
  if (required.length > 0) {
    html +=
      `  <p class="about-dim">This view's pollen data &mdash; and the open ` +
      `datasets it relies on &mdash; carry licences that require the following ` +
      `notices to be shown:</p>\n`;
    for (const text of required) {
      // Verbatim — set apart with a left rule (reuses existing color vars; no new CSS).
      html +=
        `  <p style="margin:0 0 10px;padding:6px 0 6px 12px;` +
        `border-left:3px solid var(--accent);font-size:12.5px;line-height:1.5">` +
        `${escapeHtml(text)}</p>\n`;
    }
  }
  if (credits.length > 0) {
    const items = credits.map((c) => `<li>${escapeHtml(c)}</li>`).join("\n    ");
    html +=
      `  <p class="about-dim">Supporting datasets used to build this region ` +
      `(cited as good practice):</p>\n  <ul class="about-provenance">\n    ${items}\n  </ul>\n`;
  }
  return html;
}

/**
 * The modelled-timing honesty note, scoped to the VIEWED region (F3). Shown only
 * when THIS region carries a `model` or `synthetic` tier — its dominant tier OR
 * its worst-case floor (e.g. the fill regions, all floor `synthetic`). EU/NA/Asia
 * (no model/synthetic anywhere) get nothing.
 */
function buildHonestyNote(region: AboutRegionInfo | undefined): string {
  if (!region) return "";
  const lowConf = (t?: ConfidenceTier): boolean =>
    t === "model" || t === "synthetic";
  if (!lowConf(region.tier) && !lowConf(region.floor)) return "";
  return (
    `\n  <h3>An honest note on low-confidence areas</h3>\n` +
    `  <p>Shaded <strong>low-confidence</strong> areas here are <strong>modelled ` +
    `season timing</strong> from satellite and climate data &mdash; not measured ` +
    `pollen counts or intensity. The confidence indicator marks where you are; ` +
    `treat &ldquo;Model&rdquo; / &ldquo;Synthetic&rdquo; areas as approximate.</p>\n`
  );
}

/**
 * Build the About dialog HTML for the region currently in view (`regionKey`).
 * When NO region carries a `confidence` tier, the provenance section is the EXACT
 * original hardcoded prose. When at least one region opts in, the section becomes
 * a data-driven list. The honesty note + per-region credits/attributions are
 * scoped to the VIEWED region only (empty for EU/NA/Asia which have no
 * model/synthetic fill and no satellite-derived data).
 */
function buildAboutHtml(
  regions: AboutRegionInfo[],
  regionKey: string | undefined,
): string {
  const viewed = regions.find((r) => r.key === regionKey);
  const anyTier = regions.some((r) => r.tier !== undefined);
  // The region-scoped sections (identical regardless of the provenance-list form).
  const honesty = buildHonestyNote(viewed);
  const attribution = buildAttributionSection(viewed);

  if (!anyTier) {
    // No tiered region at all: original hardcoded prose + any viewed-region
    // scoped notices (none for the original EU/NA/Asia-only load).
    return ABOUT_HEAD + ABOUT_PROVENANCE_HARDCODED + honesty + attribution + ABOUT_TAIL;
  }

  const items = regions
    .map((r) => {
      const meta = r.tier ? CONFIDENCE_META[r.tier] : undefined;
      const badge = meta
        ? `<span class="about-conf-tier" style="color:${meta.color}">` +
          `${meta.label}</span> &mdash; `
        : "";
      const detail =
        r.provenance?.method ??
        r.provenance?.honesty ??
        r.provenance?.note ??
        "see the data package provenance for sources and method.";
      return (
        `<li><strong>${escapeHtml(r.label)}</strong> &mdash; ${badge}` +
        `${escapeHtml(detail)}</li>`
      );
    })
    .join("\n    ");
  const provenance =
    `\n  <h3>How each region was built</h3>\n` +
    `  <p class="about-dim">Each region is tagged with a <strong>confidence ` +
    `tier</strong> reflecting its data source &mdash; read the magnitudes with ` +
    `that in mind:</p>\n  <ul class="about-provenance">\n    ${items}\n  </ul>\n`;
  return ABOUT_HEAD + provenance + honesty + attribution + ABOUT_TAIL;
}
